/**
 * Media section: cover image and video upload.
 */

import { type ReactNode, type RefObject } from "react";
import {
  IconCamera,
  IconPhoto,
  IconPlay,
  IconTrash,
} from "../../../../../components/Icons.tsx";
import { SmartSection } from "../SmartSection.tsx";

export interface MediaSectionProps {
  id: string;
  imageUrl: string | null;
  setImageUrl: (v: string) => void;
  bannerImageUrl?: string | null;
  setBannerImageUrl?: (v: string) => void;
  videoUrl: string | null;
  setVideoUrl: (v: string) => void;
  imageCacheBust: number;
  bannerCacheBust?: number;
  isUploadingImage: boolean;
  isUploadingBannerImage?: boolean;
  isUploadingVideo: boolean;
  videoUploadProgress: number;
  imageInputRef: RefObject<HTMLInputElement>;
  bannerInputRef?: RefObject<HTMLInputElement>;
  videoInputRef: RefObject<HTMLInputElement>;
  onImageUpload: (file: File) => void;
  onBannerImageUpload?: (file: File) => void;
  onVideoUpload: (file: File) => void;
  icon: ReactNode;
  summary: string;
  filledCount: number;
  totalCount: number;
  hint?: string;
}

export function MediaSection({
  id,
  imageUrl,
  setImageUrl,
  bannerImageUrl,
  setBannerImageUrl,
  videoUrl,
  setVideoUrl,
  imageCacheBust,
  bannerCacheBust = 0,
  isUploadingImage,
  isUploadingBannerImage = false,
  isUploadingVideo,
  videoUploadProgress,
  imageInputRef,
  bannerInputRef,
  videoInputRef,
  onImageUpload,
  onBannerImageUpload,
  onVideoUpload,
  icon,
  summary,
  filledCount,
  totalCount,
  hint,
}: MediaSectionProps) {
  const showBanner =
    setBannerImageUrl != null &&
    bannerInputRef != null &&
    onBannerImageUpload != null;

  return (
    <SmartSection
      id={id}
      title="Медиа"
      icon={icon}
      summary={summary}
      filledCount={filledCount}
      totalCount={totalCount}
      hint={hint}
      defaultOpen={false}
    >
      <div className="space-y-4">
        <div>
          <p className="text-[13px] text-slate-500 mb-3">Обложка</p>
          <input
            ref={imageInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onImageUpload(file);
            }}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => {
              if (!isUploadingImage) imageInputRef.current?.click();
            }}
            disabled={isUploadingImage}
            className="relative w-full aspect-[2/1] rounded-xl overflow-hidden bg-slate-100 border-2 border-dashed border-slate-200 active:border-slate-300 transition-colors disabled:opacity-90 flex items-center justify-center"
          >
            {imageUrl && !isUploadingImage ? (
              <>
                <img
                  src={
                    imageCacheBust
                      ? `${imageUrl}${imageUrl.includes("?") ? "&" : "?"}_t=${imageCacheBust}`
                      : imageUrl
                  }
                  alt="Обложка"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <span className="absolute bottom-0 left-0 right-0 py-2 px-3 bg-black/50 text-white text-[13px] font-medium flex items-center justify-center gap-2">
                  <IconCamera className="w-4 h-4" />
                  Заменить
                </span>
              </>
            ) : isUploadingImage ? (
              <div className="flex flex-col items-center justify-center gap-2 text-slate-500">
                <div className="w-8 h-8 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
                <span className="text-[13px]">Загрузка…</span>
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center gap-2 text-slate-400">
                <IconPhoto className="w-10 h-10" />
                <span className="text-[13px]">Добавить обложку</span>
              </div>
            )}
          </button>
          {imageUrl && !isUploadingImage && (
            <button
              type="button"
              onClick={() => setImageUrl("")}
              className="mt-2 flex items-center gap-1 text-[13px] text-red-500 active:text-red-600"
            >
              <IconTrash className="w-4 h-4" />
              Удалить
            </button>
          )}
        </div>

        {showBanner && (
          <div>
            <p className="text-[13px] text-slate-500 mb-3">
              Баннер карусели (горизонтальный)
            </p>
            <input
              ref={bannerInputRef}
              type="file"
              accept="image/jpeg,image/png,image/webp"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (file) onBannerImageUpload(file);
              }}
              className="hidden"
            />
            <button
              type="button"
              onClick={() => {
                if (!isUploadingBannerImage) bannerInputRef?.current?.click();
              }}
              disabled={isUploadingBannerImage}
              className="relative w-full aspect-[2/1] rounded-xl overflow-hidden bg-slate-100 border-2 border-dashed border-slate-200 active:border-slate-300 transition-colors disabled:opacity-90 flex items-center justify-center"
            >
              {bannerImageUrl && !isUploadingBannerImage ? (
                <>
                  <img
                    src={
                      bannerCacheBust
                        ? `${bannerImageUrl}${bannerImageUrl.includes("?") ? "&" : "?"}_t=${bannerCacheBust}`
                        : bannerImageUrl
                    }
                    alt="Баннер"
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <span className="absolute bottom-0 left-0 right-0 py-2 px-3 bg-black/50 text-white text-[13px] font-medium flex items-center justify-center gap-2">
                    <IconCamera className="w-4 h-4" />
                    Заменить
                  </span>
                </>
              ) : isUploadingBannerImage ? (
                <div className="flex flex-col items-center justify-center gap-2 text-slate-500">
                  <div className="w-8 h-8 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
                  <span className="text-[13px]">Загрузка…</span>
                </div>
              ) : (
                <div className="flex flex-col items-center justify-center gap-2 text-slate-400">
                  <IconPhoto className="w-10 h-10" />
                  <span className="text-[13px]">Баннер для карусели на главной</span>
                </div>
              )}
            </button>
            {bannerImageUrl && !isUploadingBannerImage && (
              <button
                type="button"
                onClick={() => setBannerImageUrl("")}
                className="mt-2 flex items-center gap-1 text-[13px] text-red-500 active:text-red-600"
              >
                <IconTrash className="w-4 h-4" />
                Удалить
              </button>
            )}
          </div>
        )}

        <div>
          <p className="text-[13px] text-slate-500 mb-3">Видео</p>
          <input
            ref={videoInputRef}
            type="file"
            accept="video/mp4,video/webm,video/quicktime,.mov"
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) onVideoUpload(file);
            }}
            className="hidden"
          />
          {videoUrl && !isUploadingVideo ? (
            <div className="space-y-2">
              <div className="relative w-full aspect-square rounded-xl overflow-hidden bg-slate-900">
                <video
                  src={videoUrl}
                  poster={imageUrl || undefined}
                  className="w-full h-full object-cover"
                  controls
                  playsInline
                  preload="none"
                />
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => videoInputRef.current?.click()}
                  className="text-[13px] text-slate-600 active:text-slate-800"
                >
                  Заменить
                </button>
                <button
                  type="button"
                  onClick={() => setVideoUrl("")}
                  className="text-[13px] text-red-500 active:text-red-600"
                >
                  Удалить
                </button>
              </div>
            </div>
          ) : isUploadingVideo ? (
            <div className="w-full aspect-square rounded-xl bg-slate-100 flex flex-col items-center justify-center gap-2">
              <div className="w-8 h-8 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
              <span className="text-[13px] text-slate-500">
                {videoUploadProgress}%
              </span>
              <div className="w-32 h-1.5 bg-slate-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-[var(--color-accent)] rounded-full transition-[width]"
                  style={{ width: `${videoUploadProgress}%` }}
                />
              </div>
            </div>
          ) : (
            <button
              type="button"
              onClick={() => videoInputRef.current?.click()}
              className="w-full aspect-square rounded-xl bg-slate-100 border-2 border-dashed border-slate-200 flex flex-col items-center justify-center gap-2 text-slate-400 active:border-slate-300 transition-colors"
            >
              <span className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center">
                <IconPlay className="w-5 h-5 ml-0.5" />
              </span>
              <span className="text-[13px]">Добавить видео</span>
            </button>
          )}
        </div>
      </div>
    </SmartSection>
  );
}
